<template>
  <div class="stats-graph gap-1">
    <div
      :style="`height: ${bar.percentage}%`"
      v-tooltip="{ content: bar.tooltip, html: true }"
      v-for="(bar, index) in bars"
      :class="bar.highlighted ? 'leading' : ''"
      :key="rand + index" />
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue';

export interface StatsGraphDataItem {
  label: string;
  count: number;
  // overrides the default of highlighting the bar(s) with the highest count
  highlighted?: boolean;
  // shown in the tooltip instead of the count
  countLabel?: string;
}

const rand = Math.random();
const minBarPercentage = 20;
const remainingPercentage = 100 - minBarPercentage;
const props = withDefaults(
  defineProps<{
    data: StatsGraphDataItem[];
    maxVal?: number;
    height?: string;
    barWidth?: string;
    barMaxWidth?: string;
    margin?: string;
    showLabel?: (label: string | number) => string | number;
    showCount?: (val: number) => string | number;
    highlightValue?: (stats: StatsGraphDataItem[]) => string | number;
  }>(),
  {
    barWidth: '100%',
    barMaxWidth: '100%',
    height: '50px',
    margin: '0 0 0 30px',
    showLabel: (label: string | number) => label,
    showCount: (count: number) => count,
  }
);

const renderTooltip = (dataItem: StatsGraphDataItem) =>
  `<p class="text-center m-0">
		${dataItem.label ? `<span>${props.showLabel(dataItem.label)}</span><br/>` : ''}
		<span class="fw-bold">${dataItem.countLabel ?? props.showCount(dataItem.count)}</span>
	</p>`;

const calcPercentage = (item: StatsGraphDataItem) => ({
  value: item.count,
  // Division with 0 is not allowed, we must set it to 1 in order to work
  percentage: (item.count / (getMaxValue(props.data) || 1)) * remainingPercentage + minBarPercentage,
  tooltip: renderTooltip(item),
  highlighted: item.highlighted ?? (!!highlight.value && highlight.value === item.count),
});

const highlight = computed(() => {
  return props.highlightValue?.(props.data) || getMaxValue(props.data);
});
const bars = computed(() => props.data.map((val) => calcPercentage(val)));

function getMaxValue(stats: StatsGraphDataItem[]) {
  // we are checking if it's 0 again in the template
  // if it is, we don't emphasize the bar as max
  if (!stats.length) return 0;
  if (props.maxVal) return props.maxVal;
  return Math.max(...stats.map((d) => d.count));
}
</script>
<style scoped>
.stats-graph {
  height: v-bind('props.height');
  margin: v-bind('props.margin');
  display: flex;
  align-items: flex-end;
  padding: 5px 0;
}

@media (min-width: 1200px) {
  .stats-graph {
    width: auto;
  }
}

.stats-graph > div {
  width: v-bind('props.barWidth');
  max-width: v-bind('props.barMaxWidth');
  min-width: 10px;
  height: 10%;
  height: 10%;
  background-color: var(--tblr-border-color);
  border-radius: 2px;
}

.stats-graph > div:hover {
  background-color: var(--tblr-muted);
}

.stats-graph > div.leading {
  background-color: #1bd264;
}
</style>
